import { useEffect, useState, useMemo } from 'react';
import clsx from 'clsx';
import {
  Users,
  UserPlus,
  Search,
  Crown,
  Trash2,
  RefreshCw,
  ChevronDown,
  X,
  AlertTriangle,
  Mail,
  Calendar,
  Shield,
  MoreHorizontal,
  CheckCircle,
} from 'lucide-react';
import { membersApi } from '../../../api/client';
import type { OrgMember, OrgInvitation, OrgRole } from '../../../api/client';
import { RoleBadge } from '../../../components/team/RoleBadge';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuthStore } from '../../../store/useAuthStore';

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getInitials(displayName: string | null, email: string): string {
  if (displayName) {
    const parts = displayName.trim().split(' ');
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : parts[0].substring(0, 2).toUpperCase();
  }
  return email.substring(0, 2).toUpperCase();
}

// ── Avatar ─────────────────────────────────────────────────────────────────────

function Avatar({ member }: { member: Pick<OrgMember, 'displayName' | 'email' | 'avatarUrl'> }) {
  const initials = getInitials(member.displayName, member.email);
  if (member.avatarUrl) {
    return (
      <img
        src={member.avatarUrl}
        alt={member.displayName ?? member.email}
        className="w-8 h-8 rounded-full object-cover flex-shrink-0"
      />
    );
  }
  return (
    <div className="w-8 h-8 rounded-full bg-brand/20 border border-brand/30 flex items-center justify-center flex-shrink-0">
      <span className="text-[11px] font-semibold text-brand">{initials}</span>
    </div>
  );
}

// ── Invite Modal ───────────────────────────────────────────────────────────────

function InviteModal({
  onClose,
  onInvited,
  currentRole,
}: {
  onClose: () => void;
  onInvited: (inv: OrgInvitation) => void;
  currentRole: OrgRole | null;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OrgRole>('member');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const availableRoles: OrgRole[] = currentRole === 'owner'
    ? ['admin', 'member', 'viewer']
    : ['member', 'viewer'];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const inv = await membersApi.invite(email.trim(), role);
      onInvited(inv);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="w-full max-w-md bg-surface-1 border border-surface-4 rounded-lg shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-4">
          <div className="flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-brand" />
            <h2 className="text-sm font-semibold text-gray-100">Invite Member</h2>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 transition-colors" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={(e) => void handleSubmit(e)} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Email address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="colleague@company.com"
              required
              autoFocus
              className="w-full px-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:border-brand/60 transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Role</label>
            <div className="relative">
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as OrgRole)}
                className="w-full appearance-none px-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-100 focus:outline-none focus:border-brand/60 transition-colors"
              >
                {availableRoles.map((r) => (
                  <option key={r} value={r} className="bg-surface-2">
                    {r.charAt(0).toUpperCase() + r.slice(1)}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none" />
            </div>
            <p className="mt-1.5 text-[11px] text-gray-600">
              {role === 'admin' && 'Can manage members, bets, bankroll, and settings.'}
              {role === 'member' && 'Can place and manage bets. Cannot change team settings.'}
              {role === 'viewer' && 'Read-only access to the organization.'}
            </p>
          </div>
          {error && (
            <div className="flex items-start gap-2 px-3 py-2.5 bg-loss/10 border border-loss/30 rounded-md">
              <AlertTriangle className="w-3.5 h-3.5 text-loss mt-0.5 flex-shrink-0" />
              <p className="text-xs text-loss">{error}</p>
            </div>
          )}
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-400 bg-surface-2 hover:bg-surface-3 border border-surface-4 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !email.trim()}
              className="flex-1 px-4 py-2 text-sm font-medium text-surface-1 bg-brand hover:bg-brand/90 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Sending…' : 'Send Invite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Change Role Modal ──────────────────────────────────────────────────────────

function ChangeRoleModal({
  member,
  currentUserRole,
  onClose,
  onChanged,
}: {
  member: OrgMember;
  currentUserRole: OrgRole | null;
  onClose: () => void;
  onChanged: (userId: string, role: OrgRole) => void;
}) {
  const [role, setRole] = useState<OrgRole>(member.role);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const availableRoles: OrgRole[] = currentUserRole === 'owner'
    ? ['owner', 'admin', 'member', 'viewer']
    : ['admin', 'member', 'viewer'];

  const handleSubmit = async () => {
    if (role === member.role) { onClose(); return; }
    setLoading(true);
    setError(null);
    try {
      await membersApi.updateRole(member.userId, role);
      onChanged(member.userId, role);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="w-full max-w-sm bg-surface-1 border border-surface-4 rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-4">
          <h2 className="text-sm font-semibold text-gray-100">Change Role</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 transition-colors" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="flex items-center gap-3 pb-2 border-b border-surface-4">
            <Avatar member={member} />
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-100 truncate">{member.displayName ?? member.email}</p>
              <p className="text-xs text-gray-500 truncate">{member.email}</p>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">New role</label>
            <div className="relative">
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as OrgRole)}
                className="w-full appearance-none px-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-100 focus:outline-none focus:border-brand/60 transition-colors"
              >
                {availableRoles.map((r) => (
                  <option key={r} value={r} className="bg-surface-2">
                    {r.charAt(0).toUpperCase() + r.slice(1)}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none" />
            </div>
          </div>
          {error && <p className="text-xs text-loss">{error}</p>}
          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 px-4 py-2 text-sm font-medium text-gray-400 bg-surface-2 hover:bg-surface-3 border border-surface-4 rounded-md transition-colors">
              Cancel
            </button>
            <button
              onClick={() => void handleSubmit()}
              disabled={loading}
              className="flex-1 px-4 py-2 text-sm font-medium text-surface-1 bg-brand hover:bg-brand/90 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Remove Member Modal ────────────────────────────────────────────────────────

function RemoveMemberModal({
  member,
  onClose,
  onRemoved,
}: {
  member: OrgMember;
  onClose: () => void;
  onRemoved: (userId: string) => void;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    try {
      await membersApi.removeMember(member.userId);
      onRemoved(member.userId);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove member');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="w-full max-w-sm bg-surface-1 border border-surface-4 rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-loss" />
            <h2 className="text-sm font-semibold text-gray-100">Remove Member</h2>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-gray-400">
            Remove <span className="text-gray-100 font-medium">{member.displayName ?? member.email}</span> from
            the organization? This action cannot be undone.
          </p>
          {error && <p className="text-xs text-loss">{error}</p>}
          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 px-4 py-2 text-sm font-medium text-gray-400 bg-surface-2 hover:bg-surface-3 border border-surface-4 rounded-md transition-colors">
              Cancel
            </button>
            <button
              onClick={() => void handleConfirm()}
              disabled={loading}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-loss hover:bg-loss/90 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Removing…' : 'Remove'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Transfer Ownership Modal ───────────────────────────────────────────────────

function TransferOwnershipModal({
  members,
  currentUserId,
  onClose,
  onTransferred,
}: {
  members: OrgMember[];
  currentUserId: string;
  onClose: () => void;
  onTransferred: () => void;
}) {
  const [targetId, setTargetId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const candidates = members.filter((m) => m.userId !== currentUserId && m.role !== 'owner');

  const handleConfirm = async () => {
    if (!targetId) return;
    setLoading(true);
    setError(null);
    try {
      await membersApi.transferOwnership(targetId);
      onTransferred();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to transfer ownership');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="w-full max-w-sm bg-surface-1 border border-surface-4 rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-4">
          <div className="flex items-center gap-2">
            <Crown className="w-4 h-4 text-gold" />
            <h2 className="text-sm font-semibold text-gray-100">Transfer Ownership</h2>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-gray-400">
            Transfer ownership to another member. You will be demoted to admin.
          </p>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Transfer to</label>
            <div className="relative">
              <select
                value={targetId}
                onChange={(e) => setTargetId(e.target.value)}
                className="w-full appearance-none px-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-100 focus:outline-none focus:border-brand/60 transition-colors"
              >
                <option value="" className="bg-surface-2">Select member…</option>
                {candidates.map((m) => (
                  <option key={m.userId} value={m.userId} className="bg-surface-2">
                    {m.displayName ?? m.email}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none" />
            </div>
          </div>
          {error && <p className="text-xs text-loss">{error}</p>}
          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 px-4 py-2 text-sm font-medium text-gray-400 bg-surface-2 hover:bg-surface-3 border border-surface-4 rounded-md transition-colors">
              Cancel
            </button>
            <button
              onClick={() => void handleConfirm()}
              disabled={loading || !targetId}
              className="flex-1 px-4 py-2 text-sm font-medium text-surface-0 bg-gold hover:bg-gold/80 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Transferring…' : 'Transfer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Members Table ──────────────────────────────────────────────────────────────

type MemberModal =
  | { type: 'invite' }
  | { type: 'change_role'; member: OrgMember }
  | { type: 'remove'; member: OrgMember }
  | { type: 'transfer' };

export function AdminMembersPage() {
  const { can, hasRole, role: currentRole } = usePermissions();
  const currentUserId = useAuthStore((s) => s.user?.id ?? '');

  const canInvite  = can('members.invite');
  const canRemove  = can('members.remove');
  const canReassign = can('members.update_role');
  const canTransfer = hasRole(['owner']);

  const [members,  setMembers]  = useState<OrgMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error,    setError]    = useState<string | null>(null);
  const [search,   setSearch]   = useState('');
  const [modal,    setModal]    = useState<MemberModal | null>(null);
  const [actionMenu, setActionMenu] = useState<string | null>(null);
  const [success,  setSuccess]  = useState<string | null>(null);

  const load = () => {
    setIsLoading(true);
    membersApi
      .listMembers()
      .then(setMembers)
      .catch((err) => setError((err as Error).message ?? 'Failed to load members'))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return members.filter(
      (m) =>
        m.email.toLowerCase().includes(q) ||
        (m.displayName ?? '').toLowerCase().includes(q),
    );
  }, [members, search]);

  const handleRoleChanged = (userId: string, role: OrgRole) => {
    setMembers((prev) => prev.map((m) => m.userId === userId ? { ...m, role } : m));
    setSuccess('Role updated successfully.');
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleRemoved = (userId: string) => {
    setMembers((prev) => prev.filter((m) => m.userId !== userId));
    setSuccess('Member removed.');
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleTransferred = () => {
    load();
    setSuccess('Ownership transferred.');
    setTimeout(() => setSuccess(null), 4000);
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-100">Members</h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage organization membership and roles.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {canTransfer && (
            <button
              onClick={() => setModal({ type: 'transfer' })}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gold bg-gold/10 border border-gold/20 rounded-md hover:bg-gold/20 transition-colors"
            >
              <Crown className="w-3.5 h-3.5" />
              Transfer Ownership
            </button>
          )}
          {canInvite && (
            <button
              onClick={() => setModal({ type: 'invite' })}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-surface-1 bg-brand hover:bg-brand/90 rounded-md transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              Invite Member
            </button>
          )}
        </div>
      </div>

      {/* Feedback banners */}
      {success && (
        <div className="flex items-center gap-3 p-3 bg-win/10 border border-win/20 rounded-lg">
          <CheckCircle className="w-4 h-4 text-win flex-shrink-0" />
          <p className="text-sm text-win">{success}</p>
        </div>
      )}
      {error && (
        <div className="flex items-center gap-3 p-3 bg-loss/10 border border-loss/20 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-loss flex-shrink-0" />
          <p className="text-sm text-loss">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-loss/60 hover:text-loss" aria-label="Dismiss">×</button>
        </div>
      )}

      {/* Search + refresh */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-600 pointer-events-none" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search members…"
            className="w-full pl-8 pr-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:border-brand/60 transition-colors"
          />
        </div>
        <button
          onClick={load}
          className="p-2 text-gray-500 hover:text-gray-300 hover:bg-surface-3 rounded-md transition-colors"
          aria-label="Refresh"
        >
          <RefreshCw className={clsx('w-4 h-4', isLoading && 'animate-spin')} />
        </button>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-8 px-5">
            <RefreshCw className="w-4 h-4 animate-spin" />
            Loading members…
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center py-12 text-gray-500 gap-2">
            <Users className="w-8 h-8 text-gray-700" />
            <p className="text-sm">{search ? 'No members match your search.' : 'No members found.'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" role="table">
              <thead>
                <tr className="border-b border-surface-4">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Member</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Joined</span>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    <span className="flex items-center gap-1"><Mail className="w-3 h-3" />Email</span>
                  </th>
                  <th className="w-10 px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((member) => {
                  const isSelf = member.userId === currentUserId;
                  const canAct = !isSelf && (canReassign || canRemove);
                  return (
                    <tr
                      key={member.userId}
                      className="border-b border-surface-4/50 last:border-0 hover:bg-surface-2/30 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar member={member} />
                          <div className="min-w-0">
                            <p className="font-medium text-gray-100 truncate">
                              {member.displayName ?? member.email}
                              {isSelf && <span className="ml-1.5 text-[10px] text-gray-600">(you)</span>}
                            </p>
                            <p className="text-xs text-gray-500 truncate md:hidden">{member.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <RoleBadge role={member.role} />
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 hidden sm:table-cell">
                        {formatDate(member.joinedAt)}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 hidden md:table-cell truncate max-w-[200px]">
                        {member.email}
                      </td>
                      <td className="px-4 py-3 relative">
                        {canAct && (
                          <>
                            <button
                              onClick={() => setActionMenu(actionMenu === member.userId ? null : member.userId)}
                              className="p-1.5 rounded text-gray-600 hover:text-gray-300 hover:bg-surface-3 transition-colors"
                              aria-label="Member actions"
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </button>
                            {actionMenu === member.userId && (
                              <div
                                className="absolute right-4 top-full mt-1 w-44 bg-surface-1 border border-surface-4 rounded-lg shadow-xl z-10 py-1"
                                onMouseLeave={() => setActionMenu(null)}
                              >
                                {canReassign && (
                                  <button
                                    onClick={() => { setModal({ type: 'change_role', member }); setActionMenu(null); }}
                                    className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-gray-300 hover:bg-surface-3 transition-colors"
                                  >
                                    <Shield className="w-3.5 h-3.5" />
                                    Change Role
                                  </button>
                                )}
                                {canRemove && (
                                  <button
                                    onClick={() => { setModal({ type: 'remove', member }); setActionMenu(null); }}
                                    className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-loss hover:bg-surface-3 transition-colors"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                    Remove Member
                                  </button>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-xs text-gray-600 text-center">
        {filtered.length} {filtered.length === 1 ? 'member' : 'members'}
        {members.length !== filtered.length && ` of ${members.length}`}
      </p>

      {/* Modals */}
      {modal?.type === 'invite' && (
        <InviteModal
          onClose={() => setModal(null)}
          onInvited={(inv) => {
            setSuccess(`Invitation sent to ${inv.email}.`);
            setTimeout(() => setSuccess(null), 4000);
          }}
          currentRole={currentRole}
        />
      )}
      {modal?.type === 'change_role' && (
        <ChangeRoleModal
          member={modal.member}
          currentUserRole={currentRole}
          onClose={() => setModal(null)}
          onChanged={handleRoleChanged}
        />
      )}
      {modal?.type === 'remove' && (
        <RemoveMemberModal
          member={modal.member}
          onClose={() => setModal(null)}
          onRemoved={handleRemoved}
        />
      )}
      {modal?.type === 'transfer' && (
        <TransferOwnershipModal
          members={members}
          currentUserId={currentUserId}
          onClose={() => setModal(null)}
          onTransferred={handleTransferred}
        />
      )}
    </div>
  );
}
